// ─────────────────────────────────────────────────────────────────────────────
// Activity Log Module — TypeScript Models
// ─────────────────────────────────────────────────────────────────────────────

// ── Enums ────────────────────────────────────────────────────────────────────

export enum WorkflowStatus {
  Success = 'completed',
  Failed = 'failed',
  InProgress = 'inprogress',
  Queued = 'queued',
  Pending = 'pending',
  Skipped = 'skipped',
}

export enum SortOrder {
  Asc = 'asc',
  Desc = 'desc',
}

export enum SortBy {
  CreatedAt = 'createdAt',
  UpdatedAt = 'updatedAt',
  Description = 'description',
  Status = 'status',
}

// ── API response shapes ───────────────────────────────────────────────────────

export interface IActivityTask {
  id: string;
  name: string;
  description: string;
  type?: string;
  status: string;
  message?: string;
  createdAt: string;
  updatedAt: string;
  retry: boolean;
}

export interface IQueuedTask {
  id: string;
  name: string;
  description: string;
}

export interface IActivityModifiedDataItem {
  field?: string | null;
  value?: string | null;
}

export interface IActivityDetails {
  modifiedData: IActivityModifiedDataItem[];
}

export interface IActivityItem {
  id: string;
  name: string;
  description: string;
  customerId: string;
  customerName: string;
  siteId: string;
  siteName: string;
  status: WorkflowStatus;
  correlationId: string;
  parentWorkflowId: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
  tasks: IActivityTask[];
  queuedOn?: IQueuedTask[];
  details?: IActivityDetails | string | null;
}

export interface IActivityPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface IActivityQueryResponse {
  items: IActivityItem[];
  pagination: IActivityPagination;
}

// ── Request shape ─────────────────────────────────────────────────────────────

export interface IActivityFilters {
  customerIds: string[];
  siteIds: string[];
  activity: string[];
  statuses: string[];
  userIds: string[];
  searchText: string;
  includeQueued: boolean;
}

export interface IActivityQueryRequest {
  customerIds: string[];
  siteIds: string[];
  activity: string[];
  statuses: string[];
  userIds: string[];
  searchText: string;
  includeQueued: boolean;
  pagination: {
    page: number;
    pageSize: number;
  };
  sortBy: SortBy;
  sortOrder: SortOrder;
}

// ── User display name shapes ─────────────────────────────────────────────────

export interface IUserDisplayName {
  id: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface IUserDisplayNamesMetadata {
  total: number;
}

export interface IUserDisplayNamesResponse {
  metadata: IUserDisplayNamesMetadata;
  resources: IUserDisplayName[];
}

// ── Redux state shape ─────────────────────────────────────────────────────────

export interface IActivitySort {
  sortBy: SortBy;
  sortOrder: SortOrder;
}

export interface IActivityError {
  type: 'fetch';
  message: string;
  statusCode?: number;
}

// ── Filter options ────────────────────────────────────────────────────────────

export interface IFilterSite {
  id: string;
  name: string;
}

export interface IFilterActivity {
  id: string;
  description: string;
}

export interface IFilterOptions {
  sites: IFilterSite[];
  activities: IFilterActivity[];
  statuses: string[];
  createdByIds: string[];
}

export interface IFilterOptionsRequest {
  customerIds: string[];
  siOrgId?: string;
  siteIds?: string[];
}

export interface IAccessibleCustomerOption {
  id: string;
  name: string;
}

export interface IAccessibleCustomersCacheEntry {
  fetchedAt: number;
  customers: IAccessibleCustomerOption[];
}

// ── Column config ─────────────────────────────────────────────────────────────

export interface IColumnConfig {
  order: string[];
  visibility: Record<string, boolean>;
}

// ── Redux state ───────────────────────────────────────────────────────────────

export interface IActivityState {
  items: IActivityItem[];
  selectedItem: IActivityItem | null;
  pagination: IActivityPagination;
  filters: IActivityFilters;
  sort: IActivitySort;
  isLoading: boolean;
  error: IActivityError | null;
  filterOptions: IFilterOptions | null;
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  columnConfig: IColumnConfig;
  retryingTaskIds: string[];
  retryingWorkflowIds: string[];
  hasLoadedOnce: boolean;
  customerId: string;
  customerIds: string[];
  accessibleCustomers: IAccessibleCustomerOption[];
  accessibleCustomersByOrgCache?: Record<string, IAccessibleCustomersCacheEntry>;
  accessibleCustomersLoading: boolean;
  accessibleCustomersError: string | null;
  pendingToast: { type: 'success' | 'error'; message: string } | null;
  userDisplayNames: Record<string, string>;
  isLoadingUserNames: boolean;
  userNamesError: boolean;
}
